import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import { Paragraph, Spacer, TextButton } from '@zillow/constellation';
import PropTypes from 'prop-types';
import { yieldCallback } from '@zillow/yield-callback';
import { FieldConfig } from './RefinanceCalculator.utils.js';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import TextField from '../Fields/TextField.js';
import CheckboxField from '../Fields/CheckboxField.js';
import LegalPopover from '../CalculatorForm/LegalPopover.js';

function RefinanceFields(_ref) {
  var className = _ref.className;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Current loan amount",
    leftAdornment: "$",
    name: FieldConfig.loanAmount.name,
    config: FieldConfig.loanAmount.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Current loan amount',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Enter the remaining balance of your current loan.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Interest rate",
    rightAdornment: "%",
    name: FieldConfig.rate.name,
    config: FieldConfig.rate.config,
    autoComplete: "off",
    tooltip: {
      title: 'Interest rate',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The default interest rate is based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. You may enter the current interest rate on your home loan in this field.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Current term",
    name: FieldConfig.term.name,
    rightAdornment: "months",
    config: FieldConfig.term.config,
    autoComplete: "off",
    tooltip: {
      title: 'Current term',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Your current term is the number of months originally set by your lender to pay off your existing mortgage (e.g., 30 years, 20 years, 15 years, etc.). To convert your current term from years to months, multiply the number of years by 12. For instance, a 30-year mortgage term is equal to 360 months.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Origination year",
    name: FieldConfig.originationYear.name,
    config: FieldConfig.originationYear.config,
    autoComplete: "off",
    tooltip: {
      title: 'Origination year',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Enter the year you acquired your current mortgage.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "New loan amount",
    leftAdornment: "$",
    name: FieldConfig.newLoanAmount.name,
    config: FieldConfig.newLoanAmount.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'New loan amount',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The new loan balance can be lower or higher if you'd like to pay down the principal or take cash out.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "New interest rate",
    rightAdornment: "%",
    name: FieldConfig.newRate.name,
    config: FieldConfig.newRate.config,
    autoComplete: "off",
    tooltip: {
      title: 'New interest rate',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The default interest rate is based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. You may enter a different refi interest rate in this field.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "New term",
    name: FieldConfig.newTerm.name,
    rightAdornment: "months",
    config: FieldConfig.newTerm.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'New term',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Your new term is the number of months set for you to pay off your new loan, which replaces your existing mortgage during refinancing. The new term may or may not be the same as your current term. To convert your new term from years to months, multiply the number of years by 12. For instance, a 30-year mortgage term is equal to 360 months.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Refinance fees",
    leftAdornment: "$",
    name: FieldConfig.fees.name,
    config: FieldConfig.fees.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Refinance fees',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Like all home loans, a refinance loan requires closing costs to pay for things like the loan origination fee, title and appraisal. You can roll the fees into the new loan under \"Advanced.\"")
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: yieldCallback(function () {
      return setAdvanced(!advanced);
    })
  }, advanced ? 'Simple' : 'Advanced')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextField, {
    label: "Cash out",
    leftAdornment: "$",
    name: FieldConfig.cashOut.name,
    config: FieldConfig.cashOut.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Cash out',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "If you have enough equity in your home, you may be able to take cash out during a refinance.")
    }
  }), /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Roll fees into new loan",
    name: FieldConfig.rollFees.name,
    tooltip: {
      title: 'Roll fees into new loan',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "If you want to avoid any upfront costs of refinancing, you can include those costs as part of your new loan amount to be paid as part of your future mortgage payments. See how those costs will affect your savings.")
    }
  })), /*#__PURE__*/React.createElement(LegalPopover, {
    triggerText: "Calculator disclaimer",
    text: "The refinance calculator is intended to be used for educational purposes only. Savings are not guaranteed; savings are dependent upon your current loan terms, credit profile, and available lender loan terms. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and does not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend."
  }));
}
RefinanceFields.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string
} : {};
RefinanceFields.defaultProps = {
  className: undefined
};

export { RefinanceFields as default };
